import React from "react";
import type { SelectedQuantity } from "./useSelectedQuantities";
import {
  isActiveAnalysis,
  type ActiveAnalysis,
  type AnalysisStatus,
} from "../../api/useActiveAnalysesStream";
import type { Analysis } from "../../api/Analysis";
import type { AnalysisQuantityValue } from "../../api/useAllAnalyses";
import type { IndexedFailureValues } from "../../api/useIndexedFailureValues";
import type { Setup } from "../../api/useSetups";
import { useSetups } from "../../api/useSetups";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import getQuantitySigma from "../../api/getQuantitySigma";
import { useMetrologyOverrides, type MetrologyOverrides } from "../../api/useMetrologyOverrides";
import { useDisplayInvalidPoints } from "./Analysis/ToggleDisplayInvalidPoints";
import { type Quantity } from "../../api/useQuantities";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import { isInPeriod } from "@ui/Highcharts/hooks/usePeriod";
import { isAnalysisQuantityValid } from "./metrology";
export interface Interval {
  from: number;
  to: number;
}
export type ChartType =
  | "value"
  | "measures"
  | "std"
  | "amplitude"
  | "gap"
  | "normalizedGap"
  | "diagnosis"
  | "summary";
export type DataChartType = Exclude<ChartType, "diagnosis" | "summary">;

function hasQuantityData(
  quantityValue: AnalysisQuantityValue,
  chartType: ChartType,
  sigma: number | null,
  valid: boolean,
  displayInvalidPoints: boolean,
) {
  const shouldExcludeInvalidPoints = !displayInvalidPoints && !valid;
  switch (chartType) {
    case "value":
    case "measures":
      return shouldExcludeInvalidPoints ? false : true;
    case "std":
      return shouldExcludeInvalidPoints ? false : quantityValue.std !== null;
    case "amplitude":
      return shouldExcludeInvalidPoints
        ? false
        : "simulated" in quantityValue && quantityValue.simulated !== null;
    case "gap":
      return "simulated" in quantityValue && quantityValue.simulated !== null && valid;
    case "normalizedGap":
      return (
        "simulated" in quantityValue && quantityValue.simulated !== null && sigma !== null && valid
      );
    case "diagnosis":
    case "summary":
      return valid;
    default:
      throw new Error(chartType satisfies never);
  }
}

// TODO maybe can be in common with getQuantityFlagSerie logic
export function hasAnalysisQuantityData<A extends Analysis | ActiveAnalysis>(
  analysis: A,
  quantities: Quantity[],
  chartType: DataChartType,
  setups: Setup[],
  _metrologyOverrides: MetrologyOverrides,
  displayInvalidPoints: boolean,
) {
  const active = isActiveAnalysis(analysis);
  return quantities.some(quantity => {
    const quantityValue = analysis.quantityValues[quantity.id];
    if (quantityValue === undefined) return false;
    const analysisSetup = getAnalysisSetup(setups, analysis);
    const sigma = getQuantitySigma(analysisSetup, quantity.id);
    const valid = active ? isAnalysisQuantityValid(analysis, quantity) : true;
    return hasQuantityData(quantityValue, chartType, sigma, valid, displayInvalidPoints);
  });
}

export function computeNoDataInterval<A extends Analysis>(
  analyses: A[],
  hasDataFn: (analysis: A) => boolean,
) {
  if (analyses.length < 2) {
    return [];
  }

  const { intervals, current } = analyses.reduce<{
    intervals: Interval[];
    current: Interval | null;
  }>(
    (acc, analysis, index) => {
      const previousAnalysis = analyses[index - 1] ?? analysis;
      const hasData = hasDataFn(analysis);
      const isSameCycle = previousAnalysis.group === analysis.group;

      if (acc.current) {
        if (hasData || !isSameCycle) {
          // end of current interval
          const interval = {
            from: acc.current.from,
            to: isSameCycle ? analysis.date : acc.current.to,
          };
          if (interval.from !== interval.to) acc.intervals.push(interval); // do not add single point interval
          acc.current = null;
        } else {
          // extend current interval otherwise
          acc.current.to = analysis.date;
        }
      } else if (!hasData) {
        // start a new interval
        acc.current = {
          from: isSameCycle ? previousAnalysis.date : analysis.date,
          to: analysis.date,
        };
      }

      return acc;
    },
    { intervals: [], current: null },
  );

  if (current && current.from !== current.to) {
    intervals.push(current);
  }

  return intervals;
}

export function useQuantityNoDataIntervals<A extends Analysis | ActiveAnalysis>(
  analyses: A[],
  selectedQuantities: SelectedQuantity[],
  chartType: DataChartType,
  projectId: string,
) {
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: projectId,
  });
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const setups = useSetups(projectId);

  const hasDataFn = React.useCallback(
    (analysis: Analysis) =>
      hasAnalysisQuantityData(
        analysis,
        selectedQuantities.map(q => q.quantity),
        chartType,
        setups,
        metrologyOverrides,
        displayInvalidPoints,
      ),
    [selectedQuantities, chartType, setups, metrologyOverrides, displayInvalidPoints],
  );
  return React.useMemo(() => computeNoDataInterval(analyses, hasDataFn), [analyses, hasDataFn]);
}

export function useHasDataInPeriod<A extends Analysis | ActiveAnalysis>(
  analyses: A[],
  period: Period,
  hasDataFn: (analysis: A) => boolean,
) {
  return React.useMemo(
    () => analyses.some(analysis => isInPeriod(period, analysis.date) && hasDataFn(analysis)),
    [analyses, hasDataFn, period],
  );
}

export function useHasQuantityDataInPeriod<A extends Analysis | ActiveAnalysis>(
  analyses: A[],
  selectedQuantities: SelectedQuantity[],
  chartType: DataChartType,
  projectId: string,
  period: Period,
) {
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: projectId,
  });
  const metrologyOverrides = useMetrologyOverrides(projectId);
  const setups = useSetups(projectId);

  const hasDataFn = React.useCallback(
    (analysis: A) =>
      hasAnalysisQuantityData(
        analysis,
        selectedQuantities.map(q => q.quantity),
        chartType,
        setups,
        metrologyOverrides,
        displayInvalidPoints,
      ),
    [selectedQuantities, chartType, setups, metrologyOverrides, displayInvalidPoints],
  );

  return useHasDataInPeriod(analyses, period, hasDataFn);
}

const diagnosticStatuses = new Set<AnalysisStatus>(["DIAGNOSIS", "DIAGNOSIS_ERROR"]);
export function hasDiagnosticData(
  analysis: ActiveAnalysis,
  indexedFailureValues: IndexedFailureValues = {},
) {
  return (
    diagnosticStatuses.has(analysis.status) || indexedFailureValues[analysis.date] !== undefined
  );
}

export function useDiagnosticWithQuantityNoDataIntervals(
  analyses: ActiveAnalysis[],
  indexedFailureValues: IndexedFailureValues,
) {
  return React.useMemo(
    () =>
      computeNoDataInterval(analyses, analysis =>
        hasDiagnosticData(analysis, indexedFailureValues),
      ),
    [analyses, indexedFailureValues],
  );
}

export function useHasDiagnosticDataInPeriod(
  analyses: ActiveAnalysis[],
  indexedFailureValues: IndexedFailureValues,
  period: Period,
) {
  const hasDataFn = React.useCallback(
    (analysis: ActiveAnalysis) => hasDiagnosticData(analysis, indexedFailureValues),
    [indexedFailureValues],
  );

  return useHasDataInPeriod(analyses, period, hasDataFn);
}
